import React, { useCallback, useEffect, useId, useMemo, useRef, useState, } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from "@/components/ui/dialog";
import { cn, decodeHTMLEntity } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RENTCAFE_ESTIMATOR_IFRAME_ORIGIN, buildRentCafeCostEstimatorIframeUrl, canShowRentCafeFeesEmbed, plainTextFromRentCafeMessage, postRentCafeSendEstimateToEmail, } from "@/lib/rentcafeCostEstimator";
/**
 * RentCafe Cost Estimator in an iframe: postMessage for resize and send-estimate; origin checked.
 * A Close control is placed before the iframe in tab order.
 */
var IFRAME_INITIAL_MIN_HEIGHT_PX = 700;
var EMAIL_FOR_SEND_ESTIMATE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var SEND_ESTIMATE_RESPONSE_TIMEOUT_MS = 45000;
export default function FloorplanFeesDialog(_a) {
    var floorplan = _a.floorplan, costEstimatorPropertyId = _a.costEstimatorPropertyId;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(null), loadError = _c[0], setLoadError = _c[1];
    var _d = useState(IFRAME_INITIAL_MIN_HEIGHT_PX), iframeHeightPx = _d[0], setIframeHeightPx = _d[1];
    var _e = useState(""), estimateEmail = _e[0], setEstimateEmail = _e[1];
    var _f = useState("idle"), sendEstimateState = _f[0], setSendEstimateState = _f[1];
    var _g = useState(null), sendEstimateMessage = _g[0], setSendEstimateMessage = _g[1];
    var iframeRef = useRef(null);
    var closeControlRef = useRef(null);
    var sendResponseTimeoutRef = useRef(null);
    var emailFieldId = useId();
    var emailDescId = useId();
    var embedPropertyId = costEstimatorPropertyId.trim();
    var floorplanTitle = decodeHTMLEntity(floorplan.title);
    var iframeSrc = useMemo(function () {
        var _a, _b, _c;
        return buildRentCafeCostEstimatorIframeUrl({
            encryptedPropertyId: embedPropertyId,
            floorplanId: (_b = (_a = floorplan.floorplanId) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "",
            availabilityURL: (_c = floorplan.availabilityURL) !== null && _c !== void 0 ? _c : "",
            floorPlanIdPreferSource: "url_first",
            useAvailabilityURLUnitFallback: true,
            moveInDate: null,
            leaseTerm: "12",
            noheader: "1",
        });
    }, [
        embedPropertyId,
        floorplan.floorplanId,
        floorplan.availabilityURL,
    ]);
    useEffect(function () {
        if (!open) {
            setLoadError(null);
            setIframeHeightPx(IFRAME_INITIAL_MIN_HEIGHT_PX);
            setEstimateEmail("");
            setSendEstimateState("idle");
            setSendEstimateMessage(null);
            if (sendResponseTimeoutRef.current) {
                clearTimeout(sendResponseTimeoutRef.current);
                sendResponseTimeoutRef.current = null;
            }
            return;
        }
        if (!iframeSrc) {
            setLoadError("Property or floor plan information is unavailable.");
        }
        else {
            setLoadError(null);
        }
    }, [open, iframeSrc]);
    useEffect(function () {
        if (!open || !iframeSrc) {
            return;
        }
        var onMessage = function (event) {
            var _a;
            if (event.origin !== RENTCAFE_ESTIMATOR_IFRAME_ORIGIN) {
                return;
            }
            if (event.source !== ((_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
                return;
            }
            if (!event.data || typeof event.data !== "object") {
                return;
            }
            var data = event.data;
            if (data.action === "iframe-resized" &&
                typeof data.height === "number" &&
                data.height > 0) {
                setIframeHeightPx(data.height);
                return;
            }
            if (data.action === "send-estimate-response") {
                if (sendResponseTimeoutRef.current) {
                    clearTimeout(sendResponseTimeoutRef.current);
                    sendResponseTimeoutRef.current = null;
                }
                if (data.status === true) {
                    setSendEstimateState("success");
                    if (typeof data.message === "string" && data.message.trim() !== "") {
                        var asText = plainTextFromRentCafeMessage(data.message);
                        setSendEstimateMessage(asText !== "" ? asText : "Estimate emailed. Please check your inbox.");
                    }
                    else {
                        setSendEstimateMessage("Check your email for the estimate.");
                    }
                }
                else {
                    setSendEstimateState("error");
                    setSendEstimateMessage(typeof data.message === "string" && data.message.trim() !== ""
                        ? plainTextFromRentCafeMessage(data.message)
                        : "Could not send the estimate. Try again or use the options inside the fee calculator.");
                }
            }
        };
        window.addEventListener("message", onMessage);
        return function () { return window.removeEventListener("message", onMessage); };
    }, [open, iframeSrc, floorplan.id]);
    var onIframeLoad = useCallback(function () {
        var _a, _b;
        try {
            (_b = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage({ action: "request-size" }, RENTCAFE_ESTIMATOR_IFRAME_ORIGIN);
        }
        catch (_c) {
            // ignore cross-origin edge cases
        }
    }, []);
    var onSendEstimate = useCallback(function (e) {
        e.preventDefault();
        setSendEstimateMessage(null);
        var trimmed = estimateEmail.trim();
        if (trimmed === "") {
            setSendEstimateState("error");
            setSendEstimateMessage("Enter an email address.");
            return;
        }
        if (!EMAIL_FOR_SEND_ESTIMATE.test(trimmed)) {
            setSendEstimateState("error");
            setSendEstimateMessage("Enter a valid email address.");
            return;
        }
        var frame = iframeRef.current;
        if (!(frame === null || frame === void 0 ? void 0 : frame.contentWindow)) {
            setSendEstimateState("error");
            setSendEstimateMessage("The fee calculator is still loading. Try again in a few seconds.");
            return;
        }
        if (sendResponseTimeoutRef.current) {
            clearTimeout(sendResponseTimeoutRef.current);
        }
        setSendEstimateState("sending");
        postRentCafeSendEstimateToEmail(frame, trimmed);
        sendResponseTimeoutRef.current = setTimeout(function () {
            sendResponseTimeoutRef.current = null;
            setSendEstimateState(function (prev) {
                if (prev !== "sending") {
                    return prev;
                }
                setSendEstimateMessage("No response from the fee calculator. You can also send the estimate from the tools inside the calculator above.");
                return "error";
            });
        }, SEND_ESTIMATE_RESPONSE_TIMEOUT_MS);
    }, [estimateEmail]);
    if (!canShowRentCafeFeesEmbed(embedPropertyId, floorplan)) {
        return null;
    }
    return (React.createElement(Dialog, { open: open, onOpenChange: setOpen },
        React.createElement(DialogTrigger, { asChild: true },
            React.createElement(Button, { variant: "outline", className: "w-full text-center", size: "sm", type: "button", "aria-label": "Estimate your costs for ".concat(floorplanTitle) }, "See Fees Details")),
        React.createElement(DialogContent, { className: "max-w-4xl w-[95vw] sm:w-[90vw]", onOpenAutoFocus: function (e) {
                var _a;
                e.preventDefault();
                (_a = closeControlRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            } },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Fee details"),
                React.createElement(DialogDescription, null, floorplanTitle)),
            React.createElement("div", { className: "flex justify-end" },
                React.createElement(Button, { ref: closeControlRef, type: "button", variant: "secondary", className: "shrink-0 w-full sm:w-auto", onClick: function () { return setOpen(false); } }, "Close fee details")),
            open && iframeSrc && !loadError ? (React.createElement("form", { className: "flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2", onSubmit: onSendEstimate, "aria-describedby": sendEstimateMessage ? emailDescId : undefined },
                React.createElement("div", { className: "flex-1 min-w-0 space-y-1.5" },
                    React.createElement("label", { htmlFor: emailFieldId, className: "text-sm font-medium leading-none" }, "Email estimate"),
                    React.createElement(Input, { id: emailFieldId, name: "rentcafe-estimate-email", type: "email", autoComplete: "email", inputMode: "email", placeholder: "name@example.com", value: estimateEmail, onChange: function (ev) {
                            setEstimateEmail(ev.target.value);
                            if (sendEstimateState === "error" || sendEstimateState === "success") {
                                setSendEstimateState("idle");
                                setSendEstimateMessage(null);
                            }
                        }, disabled: sendEstimateState === "sending", className: "w-full" })),
                React.createElement(Button, { type: "submit", variant: "outline", className: "w-full sm:w-auto shrink-0", disabled: sendEstimateState === "sending" }, sendEstimateState === "sending" ? "Sending…" : "Send to email"))) : null,
            sendEstimateMessage ? (React.createElement("div", { id: emailDescId, role: "status", "aria-live": "polite", className: cn("rounded-md border px-3.5 py-2.5 text-sm font-medium m-0", sendEstimateState === "error" &&
                    "border-destructive/40 bg-destructive/5 text-destructive", sendEstimateState === "success" &&
                    "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/80 dark:bg-emerald-950/50 dark:text-emerald-100") }, sendEstimateMessage)) : null,
            loadError ? (React.createElement("p", { className: "text-sm text-destructive py-4", role: "alert" }, loadError)) : null,
            open && iframeSrc && !loadError ? (React.createElement("iframe", { key: "rentcafe-estimate-iframe-".concat(floorplan.id), ref: iframeRef, src: iframeSrc, onLoad: onIframeLoad, title: "RentCafe cost estimator", className: "w-full border-0 block bg-background", style: { minHeight: IFRAME_INITIAL_MIN_HEIGHT_PX, height: iframeHeightPx }, "aria-label": "RentCafe cost estimator" })) : null)));
}
